import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ToastProvider } from '@core';
import { GetAuthStateUseCase } from '@auth';
import { GetTenantComponentsStateUseCase, LoadTenantComponentsUseCase } from '@features/tenant-components';
import { GetCasePermissionsUseCase } from '../../auth/application/use-cases/get-case-permissions/get-case-permissions.use-case';
import { GetCaseRoleUseCase } from '../../auth/application/use-cases/get-case-role/get-case-role.use-case';
import { GetInvestigationPermissionsUseCase } from '../../auth/application/use-cases/get-investigation-permissions/get-investigation-permissions.use-case';
import { GetInvestigationRoleUseCase } from '../../auth/application/use-cases/get-investigation-role/get-investigation-role.use-case';

const ACCESS_DENIED_MESSAGE = "You don't have access to this area.";

export const portalAccessGuard: CanActivateFn = (_route, state) => {
  const tenantState = inject(GetTenantComponentsStateUseCase).execute();
  const loadTenantComponents = inject(LoadTenantComponentsUseCase);
  const authState = inject(GetAuthStateUseCase).execute();
  const getCasePermissions = inject(GetCasePermissionsUseCase);
  const getCaseRole = inject(GetCaseRoleUseCase);
  const getInvestigationPermissions = inject(GetInvestigationPermissionsUseCase);
  const getInvestigationRole = inject(GetInvestigationRoleUseCase);
  const toast = inject(ToastProvider);
  const router = inject(Router);

  // Redirect to the Employee Portal.
  // The toast is shown after navigation settles so it isn't cleared by the route change.
  const redirectHome = (deniedMessage?: string): false => {
    router.navigate(['/employee']).then(() => {
      if (deniedMessage) {
        toast.error('Access denied', deniedMessage);
      }
    });
    return false;
  };

  // Super Admin bypass: mirrors the backend ProductAccessFilter so a Super Admin
  // can always reach the settings screens (mounted under /cases and /investigations)
  // to enable a SKU, even when the corresponding portal is off.
  if (authState.user()?.isSuperAdmin) {
    return of(true);
  }

  const url = state.url;
  const isCases = url.startsWith('/cases');
  const isInvestigations = url.startsWith('/investigations');

  return loadTenantComponents.execute().pipe(
    switchMap(() => {
      // Tenant-level: the organization purchased this product.
      if (isCases && !tenantState.sharedServicesPortalEnabled()) {
        return of(redirectHome(ACCESS_DENIED_MESSAGE));
      }
      if (isInvestigations && !tenantState.employeeRelationsPortalEnabled()) {
        return of(redirectHome(ACCESS_DENIED_MESSAGE));
      }

      // Hydrate the other portal's authorization in the background for the navbar switcher.
      if (isCases) {
        getInvestigationPermissions.execute().pipe(catchError(() => of([]))).subscribe();
      } else if (isInvestigations) {
        getCasePermissions.execute().pipe(catchError(() => of([]))).subscribe();
      }

      // User-level: the user has a role in this product. Load the product's
      // authorization (fetch-once, cached) then check for a non-null role.
      if (isCases) {
        return getCasePermissions
          .execute()
          .pipe(map(() => (getCaseRole.execute() === null ? redirectHome(ACCESS_DENIED_MESSAGE) : true)));
      }
      if (isInvestigations) {
        return getInvestigationPermissions
          .execute()
          .pipe(map(() => (getInvestigationRole.execute() === null ? redirectHome(ACCESS_DENIED_MESSAGE) : true)));
      }

      return of(true);
    }),
    catchError(() => of(redirectHome())),
  ) as Observable<boolean>;
};
